import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Menu, MenuItem, useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

export type OverflowNavItemConfig = {
  id: string;
  isActive: boolean;
  label: string;
  onClick: () => void;
};

interface Props {
  items: OverflowNavItemConfig[];
}

const ITEM_GAP = 4;
const MORE_BUTTON_WIDTH = 52;

export const OverflowNavBar: React.FC<Props> = ({ items }) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    const probe = probeRef.current;
    if (!container || !probe) return;

    const recalculate = () => {
      const availableWidth = container.clientWidth;
      const children = Array.from(probe.children) as HTMLElement[];

      let used = 0;
      let count = 0;

      for (let i = 0; i < children.length; i++) {
        const itemWidth = children[i].offsetWidth + ITEM_GAP;
        const isLast = i === children.length - 1;
        const budget = used + itemWidth + (isLast ? 0 : MORE_BUTTON_WIDTH + ITEM_GAP);

        if (budget <= availableWidth) {
          used += itemWidth;
          count++;
        } else {
          break;
        }
      }

      setVisibleCount(count);
    };

    const ro = new ResizeObserver(recalculate);
    ro.observe(container);
    recalculate();

    return () => ro.disconnect();
  }, [items]);

  const visibleItems = items.slice(0, visibleCount);
  const overflowItems = items.slice(visibleCount);

  const pillSx = (isActive: boolean) => ({
    '&:hover': {
      backgroundColor: theme.colors.bgCard3,
      color: theme.colors.textPrimary,
    },
    alignItems: 'center',
    backgroundColor: isActive ? theme.colors.accentBg : 'transparent',
    border: `1px solid ${isActive ? theme.colors.accentBorder : 'transparent'}`,
    borderRadius: '20px',
    color: isActive ? theme.colors.accent : theme.colors.textSecondary,
    cursor: 'pointer',
    display: 'flex',
    fontSize: '14px',
    fontWeight: isActive ? 600 : 500,
    lineHeight: 1,
    minHeight: '36px',
    px: 2,
    transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
    whiteSpace: 'nowrap',
  });

  return (
    <>
      <Box
        aria-hidden
        ref={probeRef}
        sx={{
          display: 'flex',
          gap: `${ITEM_GAP}px`,
          left: 0,
          pointerEvents: 'none',
          position: 'fixed',
          top: '-9999px',
          visibility: 'hidden',
        }}
      >
        {items.map(item => (
          <Box key={item.id} sx={pillSx(item.isActive)}>
            {item.label}
          </Box>
        ))}
      </Box>

      <Box
        ref={containerRef}
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexGrow: 1,
          gap: `${ITEM_GAP}px`,
          minWidth: 0,
          ml: { lg: 8, xs: 2 },
          overflow: 'hidden',
        }}
      >
        {visibleItems.map(item => (
          <Box
            aria-current={item.isActive ? 'page' : undefined}
            key={item.id}
            onClick={item.onClick}
            sx={pillSx(item.isActive)}
          >
            {item.label}
          </Box>
        ))}

        {overflowItems.length > 0 && (
          <>
            <Box
              onClick={e => setMoreAnchorEl(e.currentTarget)}
              sx={{ ...pillSx(false), flexShrink: 0, px: 1.5 }}
            >
              <MoreHorizIcon sx={{ fontSize: '20px' }} />
            </Box>

            <Menu
              anchorEl={moreAnchorEl}
              anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
              autoFocus={false}
              onClose={() => setMoreAnchorEl(null)}
              open={Boolean(moreAnchorEl)}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: theme.colors.bgCard,
                    backgroundImage: 'none',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '16px',
                    boxShadow: theme.colors.shadowCard,
                    mt: 1.5,
                    overflow: 'hidden',
                    width: 220,
                  },
                },
              }}
              sx={{ '& .MuiList-root': { p: 0.5 } }}
              transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            >
              {overflowItems.map(item => (
                <MenuItem
                  key={item.id}
                  onClick={() => {
                    item.onClick();
                    setMoreAnchorEl(null);
                  }}
                  sx={{
                    '&:hover': { backgroundColor: theme.colors.bgHover },
                    borderRadius: '12px',
                    color: item.isActive ? theme.colors.accent : theme.colors.textSecondary,
                    fontSize: '14px',
                    fontWeight: item.isActive ? 600 : 500,
                    px: 2,
                    py: 1,
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
    </>
  );
};
